import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { BookOpenIcon, BriefcaseIcon, ChevronRightIcon, FileBadgeIcon, MapIcon, MegaphoneIcon, PawPrintIcon, PinIcon, SmartphoneIcon, TrashIcon, TriangleAlertIcon, VolleyballIcon } from 'lucide-react';
import { quickAccessLinks, type QuickAccessIcon } from '../../data/homepage';
import { AlertesDialog } from '../avis/AlertesDialog';
import { DecorCurve } from '../ui/DecorCurve';
import { useAuth } from '../../contexts/AuthContext';
const icons: Record<QuickAccessIcon, typeof MapIcon> = {
  smartphone: SmartphoneIcon,
  megaphone: MegaphoneIcon,
  book: BookOpenIcon,
  trash: TrashIcon,
  map: MapIcon,
  briefcase: BriefcaseIcon,
  ball: VolleyballIcon,
  permit: FileBadgeIcon,
  alert: TriangleAlertIcon,
  pet: PawPrintIcon
};
const rowClass = 'group flex w-full items-center gap-3 px-5 py-2.5 text-left text-sm font-semibold text-white transition-colors duration-150 ease-ville hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white';
const iconClass = 'grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-ville group-hover:bg-ville-lime';

/**
 * Le rail « Vos accès rapides » du site de Beaconsfield, où la barre de services bciti+
 * vient se fondre plutôt que de s'ajouter à côté.
 *
 * Les dix entrées sont toutes visibles : pas de défilement interne. C'est le hero qui
 * s'adapte à la hauteur du rail, et non l'inverse.
 */
export function QuickAccessPanel() {
  const {
    isAuthenticated
  } = useAuth();
  const [alertesOpen, setAlertesOpen] = useState(false);
  return <section aria-labelledby="acces-rapides" className="flex flex-1 flex-col" data-id="element-1807" data-mp="1x57z450k4sid">
      <div className="relative isolate overflow-hidden bg-ville-blue px-5 py-3.5" data-id="element-1808" data-mp="bweg47r4pzd">
        <DecorCurve variant="moon" placement="top-right" className="h-full w-1/2 text-white/15" data-id="element-1809" data-mp="3rhfsvf1573ck" />
        <h2 id="acces-rapides" className="relative z-10 flex items-center gap-3 font-display text-lg font-bold uppercase tracking-wide text-white" data-id="element-1810" data-mp="1ps8j0oe5arfp">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-ville-blue" data-id="element-1811" data-mp="1fum0zt1jujri">
            <PinIcon className="h-4 w-4" aria-hidden="true" data-id="element-1812" data-mp="1ktfehlgu6aro" />
          </span>
          Vos accès rapides
        </h2>
      </div>

      <ul className="grid flex-1 grid-cols-2 content-start bg-ville-navy py-1 sm:grid-cols-3 lg:grid-cols-1 lg:divide-y lg:divide-white/10 lg:py-0" data-id="element-1813" data-mp="28kb2e7i68ltn">
        {quickAccessLinks.map(link => {
        const Icon = icons[link.icon];
        // Sans session, « Avis publics » propose d'abord l'inscription aux alertes.
        const opensDialog = link.action === 'alertes' && !isAuthenticated;
        const inner = <>
              <span className={iconClass} aria-hidden="true" data-id="element-1814" data-mp="n81asvwt8dpj">
                <Icon className="h-3.5 w-3.5" data-id="element-1815" data-mp="s5t0rkdw9pr3" />
              </span>
              <span className="min-w-0 flex-1 leading-tight" data-id="element-1816" data-mp="36c9dx7i1dl5p">{link.label}</span>
              <ChevronRightIcon className="hidden h-4 w-4 shrink-0 text-white/40 transition-colors duration-150 ease-ville group-hover:text-white lg:block" aria-hidden="true" data-id="element-1817" data-mp="31docx5uymxg7" />
            </>;
        return <li key={link.id} data-id="element-1818" data-mp="3bcnb1ui3naoy">
              {opensDialog ? <button type="button" onClick={() => setAlertesOpen(true)} className={rowClass} data-id="element-1819" data-mp="2nlhz3qfa52jx">
                  {inner}
                </button> : link.href ? <a href={link.href} className={rowClass} data-id="element-1820" data-mp="2wecvdumrrnxk">
                  {inner}
                </a> : <Link to={link.to ?? '/'} className={rowClass} data-id="element-1821" data-mp="2rmtp3x22fpsn">
                  {inner}
                </Link>}
            </li>;
      })}
      </ul>

      <AnimatePresence data-id="element-1822" data-mp="1zwyqtaw8cpru">
        {alertesOpen && <AlertesDialog key="acces-alertes" onClose={() => setAlertesOpen(false)} data-id="element-1823" data-mp="1zw746pkm22dp" />}
      </AnimatePresence>
    </section>;
}